// @ts-nocheck
import React, { useRef, useState } from 'react';

interface ReaderSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExportOpml: () => void;
  onImportOpml: (opmlContent: string) => Promise<any>;
}

export default function ReaderSettingsModal({
  isOpen,
  onClose,
  onExportOpml,
  onImportOpml,
}: ReaderSettingsModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importStatus, setImportStatus] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    setIsImporting(true);
    setImportStatus('Reading OPML file…');

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const content = event.target?.result as string;
        setImportStatus('Importing feeds…');
        const result = await onImportOpml(content);
        setImportStatus(
          `Imported ${result.imported || 0} feeds (${result.existing || 0} were already subscribed).`
        );
      } catch (err: any) {
        setImportStatus(`Import failed: ${err.message}`);
      } finally {
        setIsImporting(false);
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="reader-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="reader-modal-card" style={{ maxWidth: '580px' }} onClick={(e) => e.stopPropagation()}>
        <div className="reader-modal-header">
          <h3 className="reader-modal-title">Reader Settings & Portability</h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--reader-text-muted)' }}
          >
            ×
          </button>
        </div>

        <div className="reader-modal-body">
          {/* OPML Portability */}
          <div>
            <h4 style={{ margin: '0 0 8px', fontSize: '0.95rem', fontWeight: 600 }}>
              OPML Feed Portability
            </h4>
            <p style={{ margin: '0 0 12px', fontSize: '0.82rem', color: 'var(--reader-text-muted)' }}>
              Easily migrate your subscriptions between Inoreader, Feedly, NetNewsWire, Readwise, and Harivishnu Reader.
            </p>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button className="reader-btn" onClick={onExportOpml} title="Export feeds to OPML">
                <i className="fi fi-rr-download" style={{ fontSize: '0.8rem' }} />
                <span>Export OPML</span>
              </button>

              <button
                className="reader-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={isImporting}
                title="Import feeds from OPML"
              >
                <i className="fi fi-rr-upload" style={{ fontSize: '0.8rem' }} />
                <span>Import OPML</span>
              </button>

              <input
                type="file"
                ref={fileInputRef}
                accept=".opml,.xml"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </div>

            {importStatus && (
              <div
                style={{
                  marginTop: '10px',
                  padding: '8px 12px',
                  background: 'var(--reader-hover)',
                  border: '1px solid var(--reader-border)',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                  color: 'var(--reader-text)',
                }}
              >
                {importStatus}
              </div>
            )}
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--reader-border)', margin: '4px 0' }} />

          {/* Keyboard Shortcuts */}
          <div>
            <h4 style={{ margin: '0 0 12px', fontSize: '0.95rem', fontWeight: 600 }}>
              Keyboard Shortcuts
            </h4>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '8px',
                fontSize: '0.82rem',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--reader-hover)', borderRadius: '3px' }}>
                <span style={{ color: 'var(--reader-text-muted)' }}>Focus Search</span>
                <kbd style={{ fontFamily: 'var(--reader-font-mono)', fontWeight: 600 }}>/</kbd>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--reader-hover)', borderRadius: '3px' }}>
                <span style={{ color: 'var(--reader-text-muted)' }}>Next Item</span>
                <kbd style={{ fontFamily: 'var(--reader-font-mono)', fontWeight: 600 }}>j</kbd>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--reader-hover)', borderRadius: '3px' }}>
                <span style={{ color: 'var(--reader-text-muted)' }}>Previous Item</span>
                <kbd style={{ fontFamily: 'var(--reader-font-mono)', fontWeight: 600 }}>k</kbd>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--reader-hover)', borderRadius: '3px' }}>
                <span style={{ color: 'var(--reader-text-muted)' }}>Toggle Read/Unread</span>
                <kbd style={{ fontFamily: 'var(--reader-font-mono)', fontWeight: 600 }}>m</kbd>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--reader-hover)', borderRadius: '3px' }}>
                <span style={{ color: 'var(--reader-text-muted)' }}>Toggle Favorite</span>
                <kbd style={{ fontFamily: 'var(--reader-font-mono)', fontWeight: 600 }}>s</kbd>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--reader-hover)', borderRadius: '3px' }}>
                <span style={{ color: 'var(--reader-text-muted)' }}>Archive Item</span>
                <kbd style={{ fontFamily: 'var(--reader-font-mono)', fontWeight: 600 }}>a</kbd>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--reader-hover)', borderRadius: '3px' }}>
                <span style={{ color: 'var(--reader-text-muted)' }}>Open Original</span>
                <kbd style={{ fontFamily: 'var(--reader-font-mono)', fontWeight: 600 }}>v</kbd>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--reader-hover)', borderRadius: '3px' }}>
                <span style={{ color: 'var(--reader-text-muted)' }}>Close Pane / Modal</span>
                <kbd style={{ fontFamily: 'var(--reader-font-mono)', fontWeight: 600 }}>Esc</kbd>
              </div>
            </div>
          </div>
        </div>

        <div className="reader-modal-footer">
          <button type="button" className="reader-btn reader-btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
